import {kapitelDateString, kapitelMonthString} from "@/graphql/kapitelTypes";
import {DateTime as LuxonDateTime, Interval as LuxonInterval, WeekdayNumbers} from "luxon";
import month from "@/helper/datetime/month";


export const allEmployeeTimezone = "Europe/Berlin"
export const allEmployeeLocale = "de"
// YYYY-MM-DD
const dateRE = /^\d{4}-([0]\d|1[0-2])-([0-2]\d|3[01])$/

export const validateKapitelDate = (dateString:kapitelDateString) => {
  if (!dateRE.test(dateString)) {
      throw new Error("malformed kapitelDateString")
  }
}

// private
const kapitelDateStringToLuxon = (dateString: kapitelDateString) : LuxonDateTime => {
  validateKapitelDate(dateString)
  return LuxonDateTime.fromISO(dateString, {zone: "utc", locale: allEmployeeLocale})
}

const luxonToKapitelDateString = (luxon: LuxonDateTime) : kapitelDateString => {
    return luxon.toFormat('yyyy-MM-dd') as kapitelDateString
}

export const getToday = (): kapitelDateString => {
  const l = LuxonDateTime
      .now()
      .setZone(allEmployeeTimezone)
  return luxonToKapitelDateString(l)
}

export const formatWeekdayShortFromKapitelDate = (dateString: kapitelDateString, punctuate = true): string => {
    let abbr = formatKapitelDate(dateString, "EEE")
    if (!punctuate) {
        abbr = abbr.substring(0,2)
    }
    return abbr
}

export const formatWeekdayFromKapitelDate = (dateString: kapitelDateString): string => {
  return formatKapitelDate(dateString, "EEEE")
}

export const formatMonthFromKapitelDate = (dateString: kapitelDateString): string => {
  return formatKapitelDate(dateString, "LLLL")
}

export const getMonthFromKapitelDate = (dateString: kapitelDateString): number => {
    return Number(formatKapitelDate(dateString, "L"))
}

export const getDayOfMonthFromKapitelDate = (dateString: kapitelDateString): number => {
  return Number(formatKapitelDate(dateString, "d"))
}
export const setDayOfMonth = (dateString: kapitelDateString, day:number): kapitelDateString => {
    return luxonToKapitelDateString(kapitelDateStringToLuxon(dateString).set({day}))
}

export const getISODayOfWeekFromKapitelDate = (dateString: kapitelDateString): number => {
  return Number(formatKapitelDate(dateString, "c"))
}

export const formatISODayOfWeek = (dow : WeekdayNumbers, format: 'ccc' | 'cccc' | 'ccccc'): string => {
    return LuxonDateTime.local().set({weekday:dow}).toFormat(format, {locale: allEmployeeLocale})
}

export const getISOWeekNumberFromKapitelDate = (dateString: kapitelDateString): number => {
  return Number(formatKapitelDate(dateString, "WW"))
}

export const getYearFromKapitelDate = (dateString: kapitelDateString): number => {
  return Number(formatKapitelDate(dateString, "y"))
}

export const startOfMonth = (dateString: kapitelDateString): kapitelMonthString => {
  const dateTime = kapitelDateStringToLuxon(dateString)
  return luxonToKapitelDateString(dateTime.startOf("month")) as kapitelMonthString
}

export const startOfYear = (dateString: kapitelDateString): kapitelDateString => {
    const dateTime = kapitelDateStringToLuxon(dateString)
    return luxonToKapitelDateString(dateTime.startOf("year"))
}

export const endOfMonth = (dateString: kapitelDateString): kapitelDateString => {
  const dateTime = kapitelDateStringToLuxon(dateString)
  return luxonToKapitelDateString(dateTime.endOf("month"))
}

export const addMonths = (dateString: kapitelDateString, amount: number): kapitelDateString => {
  const dateTime = kapitelDateStringToLuxon(dateString)
  return luxonToKapitelDateString(dateTime.plus({months:amount}))
}

export const subMonths = (dateString: kapitelDateString, amount: number): kapitelDateString => {
  const dateTime = kapitelDateStringToLuxon(dateString)
  return luxonToKapitelDateString(dateTime.minus({months:amount}))
}


export const differenceInMonths = (string1: kapitelDateString, string2: kapitelDateString): number => {
  const dateTime1 = kapitelDateStringToLuxon(string1)
  const dateTime2 = kapitelDateStringToLuxon(string2)

  return dateTime1.diff(dateTime2 ).as("months")

}

export const differenceInDays = (string1: kapitelDateString, string2: kapitelDateString): number => {
    const dateTime1 = kapitelDateStringToLuxon(string1)
    const dateTime2 = kapitelDateStringToLuxon(string2)

    return dateTime1.diff(dateTime2 ).as("days")

}

export const startOfWeek = (dateString: kapitelDateString): kapitelDateString => {
  const dateTime = kapitelDateStringToLuxon(dateString)
  return luxonToKapitelDateString(dateTime.startOf("week"))
}

export const endOfWeek = (dateString: kapitelDateString): kapitelDateString => {
  const dateTime = kapitelDateStringToLuxon(dateString)
  return luxonToKapitelDateString(dateTime.endOf("week"))
}

export const addWeeks = (dateString: kapitelDateString, amount: number): kapitelDateString => {
    const dateTime = kapitelDateStringToLuxon(dateString)
    return luxonToKapitelDateString(dateTime.plus({weeks:amount}))
}

export const addDays = (dateString: kapitelDateString, amount: number): kapitelDateString => {
  const dateTime = kapitelDateStringToLuxon(dateString)
  return luxonToKapitelDateString(dateTime.plus({days:amount}))
}

export const subDays = (dateString: kapitelDateString, amount: number): kapitelDateString => {
  const dateTime = kapitelDateStringToLuxon(dateString)
  return luxonToKapitelDateString(dateTime.minus({days:amount}))
}

/* string1 is before string2 */
export const isBefore = (string1: kapitelDateString, string2: kapitelDateString): boolean =>{
  const date1 = kapitelDateStringToLuxon(string1)
  const date2 = kapitelDateStringToLuxon(string2)
  return date1.diff(date2, "days").days<0
}
/* string1 is after string2 */
export const isAfter = (string1: kapitelDateString, string2: kapitelDateString): boolean =>{
  const date1 = kapitelDateStringToLuxon(string1)
  const date2 = kapitelDateStringToLuxon(string2)
  return date1.diff(date2, "days").days>0
}
/* string1 is between [min,max] */
export const isBetween = (string1: kapitelDateString, min: kapitelDateString, max: kapitelDateString): boolean =>{
    return !isBefore(string1, min) && !isAfter(string1, max)
}
export const isSameDay = (string1: kapitelDateString, string2: kapitelDateString): boolean =>{
    return string1 === string2
}
export const isSameMonth = (string1: kapitelDateString, string2: kapitelDateString): boolean =>{
    return month.getKapitelMonthFromKapitelDate(string1) === month.getKapitelMonthFromKapitelDate(string2)
}
export const max = (...dates: kapitelDateString[]): kapitelDateString => {
    if (dates.length === 0) {
        throw new Error("max requires at least one argument");
    }
    return dates.reduce((maxDate, current) => isAfter(current, maxDate) ? current : maxDate);
}

export const min = (...dates: kapitelDateString[]): kapitelDateString => {
    if (dates.length === 0) {
        throw new Error("min requires at least one argument");
    }
    return dates.reduce((minDate, current) => isBefore(current, minDate) ? current : minDate);
}


export const isFirstDayOfMonth = (dateString: kapitelDateString): boolean => {
  return getDayOfMonthFromKapitelDate(dateString) == 1
}


export const isLastDayOfMonth = (dateString: kapitelDateString): boolean => {
  return endOfMonth(dateString) == dateString
}

export const isMonday = (dateString: kapitelDateString): boolean => {
  return getISODayOfWeekFromKapitelDate(dateString) == 1
}

export const isToday = (dateString: kapitelDateString): boolean => {

    const todayDate = kapitelDateStringToLuxon(getToday())
    const queryDate = kapitelDateStringToLuxon(dateString)

    return queryDate==todayDate

}
export const isYesterday = (dateString: kapitelDateString): boolean => {

    const todayDate = kapitelDateStringToLuxon(getToday())
    const queryDate = kapitelDateStringToLuxon(dateString)

    return queryDate.plus({days: 1})==todayDate

}
export const isTomorrow = (dateString: kapitelDateString): boolean => {

  const todayDate = kapitelDateStringToLuxon(getToday())
  const queryDate = kapitelDateStringToLuxon(dateString)

  return todayDate.plus({days: 1})==queryDate
}

export const isPast = (dateString: kapitelDateString): boolean => {
    return isAfter(getToday(), dateString)
}

export const isWeekend = (dateString: kapitelDateString): boolean => {
  const idw = getISODayOfWeekFromKapitelDate(dateString)
  return idw==6||idw==7
}

/**
 * @param dateString
 * @param format https://moment.github.io/luxon/#/formatting?id=table-of-tokens
 */
export const formatKapitelDate = (dateString: kapitelDateString, format: string = 'd. MMMM yyyy'): string => {
  return kapitelDateStringToLuxon(dateString).toFormat(format)
}

export const cmpKapitelDate = (string1: kapitelDateString, string2: kapitelDateString) => {
  // Returns -1 if string1 is less than string2; 1 if string1 is greater than string2, and 0 if they are equal.
  validateKapitelDate(string1)
  validateKapitelDate(string2)
  const date1 = new Date(string1)
  const date2 = new Date(string2)

  if(date1 < date2){
    return -1
  } else if (date2 < date1){
    return 1
  }
  return 0

}


export const weekdays = () => {
  const startOfWeek = LuxonDateTime.now().setZone(allEmployeeTimezone).startOf("week").startOf("day")
  const endOfWeek = LuxonDateTime.now().setZone(allEmployeeTimezone).endOf("week").endOf("day")
  const weekInterval = LuxonInterval.fromDateTimes(
    startOfWeek,
    endOfWeek
  )
  return weekInterval.splitBy({"days":1}).map((interval)=>{
    if(!interval.start){
      throw new Error("Invalid interval.")
    }
    const dateString = luxonToKapitelDateString(interval.start)
    return {
      day: getISODayOfWeekFromKapitelDate(dateString),
      labelShort: formatWeekdayShortFromKapitelDate(dateString),
      label: formatWeekdayFromKapitelDate(dateString)
    }
  })

}

export default {
  addDays,
  addWeeks,
  addMonths,
  allEmployeeLocale,
  allEmployeeTimezone,
  cmpKapitelDate,
  differenceInMonths,
  differenceInDays,
  endOfMonth,
  formatKapitelDate,
  formatMonthFromKapitelDate,
  formatWeekdayFromKapitelDate,
  formatWeekdayShortFromKapitelDate,
  getDayOfMonthFromKapitelDate,
  setDayOfMonth,
  getISODayOfWeekFromKapitelDate,
  getISOWeekNumberFromKapitelDate,
  formatISODayOfWeek,
  getMonthFromKapitelDate,
  getToday,
  getYearFromKapitelDate,
  isAfter,
  isBefore,
  isBetween,
  isPast,
  min,
  max,
  isFirstDayOfMonth,
  isLastDayOfMonth,
  isMonday,
  isSameDay,
  isSameMonth,
  isTomorrow,
  isToday,
  isWeekend,
  isYesterday,
  startOfMonth,
  subDays,
  subMonths,
  validateKapitelDate,
  weekdays,
  startOfWeek,
  endOfWeek,
  startOfYear
}
